import { normalizeErrorString } from '@medplum/core'
import type { Attachment } from '@medplum/fhirtypes'
import { DocumentSource } from '@seen/core/models/document-references/constants'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'

import { Button, cn } from '@seen/ui'
import {
  IconAlertCircle,
  IconCheck,
  IconFileUpload,
  IconX,
} from '@tabler/icons-react'
import pLimit from 'p-limit'
import pluralize from 'pluralize'
import { type JSX, useCallback, useEffect, useRef, useState } from 'react'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useGlobalFileUpload } from '#src/hooks/useGlobalFileUpload.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { createAttachmentFromFile } from '#src/utils/createAttachmentFromFile.ts'
import { toast } from '#src/utils/toast.ts'

interface FileWithPreview {
  id: string
  name: string
  size: number
  type: string
  lastModified: number
  originalFile: File
  preview?: string
  attachment?: Attachment
  error?: string
  uploading?: boolean
}

const generateFileId = (): string => Math.random().toString(36).substring(2, 15)

const UPLOAD_CONCURRENCY = 5

export function GlobalFileUploadArea(): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const {
    isActive: isFileUploadActive,
    hideUploadArea,
    onUploadComplete,
  } = useGlobalFileUpload({
    onUploadComplete: (documentReferences) => {
      toast.success(
        <div
          className="flex flex-col gap-2"
          data-testid="upload-success-toast"
          // Surface the created DocumentReference id when a single file was
          // uploaded so a caller (e.g. an e2e test) can open the document
          // directly by id instead of rediscovering it in the documents list.
          data-document-reference-id={
            documentReferences.length === 1
              ? documentReferences[0]?.id
              : undefined
          }
        >
          <p>
            Successfully uploaded {documentReferences.length}{' '}
            {pluralize('file', documentReferences.length)}
          </p>
          <Link to="/documents" className="underline">
            View Documents
          </Link>
        </div>
      )
    },
  })

  const [files, setFiles] = useState<FileWithPreview[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const medplum = useMedplum()
  const practitioner = useCurrentPractitioner()
  const createDocumentMutation = useCreateMutation<ExtendedDocumentReference>()

  const createFilePreview = useCallback((file: File): FileWithPreview => {
    const fileWithPreview: FileWithPreview = {
      id: generateFileId(),
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      originalFile: file,
    }

    if (file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (e) => {
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileWithPreview.id
              ? { ...f, preview: e.target?.result as string }
              : f
          )
        )
      }
      reader.readAsDataURL(file)
    }

    return fileWithPreview
  }, [])

  const handleFiles = useCallback(
    (fileList: FileList | File[]) => {
      const filesArray = Array.from(fileList)
      const filesWithPreview = filesArray.map(createFilePreview)
      setFiles((prev) => [...prev, ...filesWithPreview])
    },
    [createFilePreview]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)

      const droppedFiles = e.dataTransfer.files
      if (droppedFiles.length > 0) {
        handleFiles(droppedFiles)
      }
    },
    [handleFiles]
  )

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files)
      }
    },
    [handleFiles]
  )

  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId))
  }, [])

  const uploadFile = async (file: FileWithPreview): Promise<Attachment> => {
    try {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id ? { ...f, uploading: true, error: undefined } : f
        )
      )

      const attachment = await createAttachmentFromFile(file.originalFile, {
        medplum,
      })

      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id ? { ...f, attachment, uploading: false } : f
        )
      )

      return attachment
    } catch (error) {
      const errorMessage = normalizeErrorString(error)
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id ? { ...f, error: errorMessage, uploading: false } : f
        )
      )
      throw error
    }
  }

  const uploadAndCreateDocument = async (
    file: FileWithPreview
  ): Promise<ExtendedDocumentReference> => {
    const attachment = file.attachment ?? (await uploadFile(file))
    const document = new ExtendedDocumentReference({
      author: practitioner.reference,
      attachments: [{ size: file.size, ...attachment }],
    })
    document.source = DocumentSource.BULK_UPLOAD
    try {
      return await createDocumentMutation.mutateAsync(document)
    } catch (error) {
      const errorMessage = normalizeErrorString(error)
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id ? { ...f, error: errorMessage, uploading: false } : f
        )
      )
      throw error
    }
  }

  const handleConfirmUpload = async (): Promise<void> => {
    setIsUploading(true)
    try {
      const pendingFiles = files.filter((f) => !f.attachment || f.error)
      setFiles((prev) =>
        prev.map((f) => (f.error ? { ...f, error: undefined } : f))
      )

      const limit = pLimit(UPLOAD_CONCURRENCY)
      const results = await Promise.allSettled(
        pendingFiles.map((file) => limit(() => uploadAndCreateDocument(file)))
      )

      const succeeded = results.flatMap((r) =>
        r.status === 'fulfilled' ? [r.value] : []
      )
      if (succeeded.length > 0) {
        onUploadComplete?.(succeeded)
      }

      if (succeeded.length === pendingFiles.length) {
        setFiles([])
      } else {
        setFiles((prev) => prev.filter((f) => f.error))
      }
    } finally {
      setIsUploading(false)
    }
  }

  const hasValidFiles = files.length > 0
  const failedFiles = files.filter((f) => f.error)
  const allHaveErrors = hasValidFiles && files.every((f) => f.error)
  const pendingCount = files.filter((f) => !f.attachment || f.error).length
  const uploadLabel = allHaveErrors
    ? `Retry ${files.length} ${pluralize('file', files.length)}`
    : `Upload ${pendingCount} ${pluralize('file', pendingCount)}`

  useEffect(() => {
    const handleGlobalDragOver = (e: DragEvent): void => {
      e.preventDefault()
    }

    const handleGlobalDrop = (e: DragEvent): void => {
      e.preventDefault()
    }

    document.addEventListener('dragover', handleGlobalDragOver)
    document.addEventListener('drop', handleGlobalDrop)

    return () => {
      document.removeEventListener('dragover', handleGlobalDragOver)
      document.removeEventListener('drop', handleGlobalDrop)
    }
  }, [])

  if (isReadOnly) {
    return null
  }

  return (
    <Dialog
      open={isFileUploadActive}
      onOpenChange={(open) => {
        if (!open) {
          hideUploadArea()
        }
      }}
    >
      <DialogDisplay>
        <DialogHeader>
          <DialogTitle>Upload Files</DialogTitle>
        </DialogHeader>

        <DialogContent>
          <div className="p-6">
            <div
              className={cn(
                'rounded-lg border-2 border-dashed p-8 text-center transition-colors',
                isDragOver
                  ? 'border-foreground-secondary bg-foreground-secondary/10'
                  : 'border-foreground-secondary',
                hasValidFiles && 'mb-6'
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center gap-4">
                <div className="rounded-full border border-foreground-secondary p-3">
                  <IconFileUpload className="h-6 w-6 text-foreground-secondary" />
                </div>
                <div>
                  <p className="text-lg font-medium text-foreground">
                    Drop files here or{' '}
                    <button
                      type="button"
                      className="text-foreground-secondary underline hover:text-foreground-secondary/80"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      browse
                    </button>
                  </p>
                  <div className="mt-3 rounded-md bg-frame p-3 text-xs">
                    <p>
                      Documents will be automatically associated with
                      participants when possible. If we&apos;re unable to
                      auto-assign, a document review task will be created and
                      assigned to you.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileInputChange}
              accept="*/*"
            />

            {hasValidFiles && (
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {files.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-3 rounded-lg border p-3"
                  >
                    {file.preview && (
                      <img
                        src={file.preview}
                        alt={file.name}
                        className="h-10 w-10 rounded object-cover"
                      />
                    )}

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {file.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      {file.uploading && <Loader className="h-4 w-4" />}

                      {file.attachment && (
                        <IconCheck className="h-4 w-4 text-green-600" />
                      )}

                      {file.error && (
                        <IconAlertCircle className="h-4 w-4 text-red-600" />
                      )}

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(file.id)}
                      >
                        <IconX className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {failedFiles.length > 0 && (
              <div className="space-y-2 mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-800">
                  {failedFiles.length} {pluralize('file', failedFiles.length)}{' '}
                  failed to upload. Please remove{' '}
                  {failedFiles.length === 1 ? 'it' : 'them'} or try again.
                </p>
                <ul className="space-y-1">
                  {failedFiles.map((file) => (
                    <li
                      key={file.id}
                      className="text-xs wrap-break-word text-red-800"
                    >
                      <span className="font-medium">{file.name}:</span>{' '}
                      {file.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogFooter className="border-t">
          <Button variant="outline" onClick={hideUploadArea}>
            Cancel
          </Button>
          <Button
            data-testid="confirm-upload-button"
            onClick={handleConfirmUpload}
            disabled={!hasValidFiles || isUploading}
            className="min-w-[120px]"
            loading={isUploading}
          >
            {uploadLabel}
          </Button>
        </DialogFooter>
      </DialogDisplay>
    </Dialog>
  )
}
